import { FC, useState, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '../../../hooks/useDashboard';
import { getAgentAppointments } from '../../../utils/apis';
import { ExtractionDataResponse } from '../../../schemas/apis/interface';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import CallsDataSkeleton from '../../Calls/Skeletons/CallsDataSkeleton';
import theme from '../../../theme';

const ExtractionDataTable: FC = () => {
  const { selectedAssistantId } = useDashboard();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const { data: extractionData, isLoading } = useQuery({
    queryKey: [
      'get-agent-appointments',
      selectedAssistantId,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    queryFn: () =>
      getAgentAppointments({
        agentId: selectedAssistantId || '',
        pageNo: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    enabled: !!selectedAssistantId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 1,
    retry: 0,
    gcTime: 1000 * 60 * 10,
  });

  const extractionDataResponse = extractionData?.data as ExtractionDataResponse;
  const appointments = useMemo(
    () => extractionDataResponse?.appointments || [],
    [extractionDataResponse?.appointments]
  );
  const totalColumns = extractionDataResponse?.total || 0;

  const displayData = appointments;
  const isTableLoading = isLoading;

  const uniqueDataKeys = useMemo(
    () =>
      Array.from(
        displayData.reduce((keys: Set<string>, appointment: any) => {
          Object.keys(appointment.data || {}).forEach(key => keys.add(key));
          return keys;
        }, new Set<string>())
      ),
    [displayData]
  );

  const rows = useMemo(
    () =>
      displayData.map((appointment: any) => ({
        id: appointment._id,
        chatId: appointment.chatId,
        createdAt: new Date(appointment.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }),
        ...appointment.data,
        actions: appointment._id,
      })),
    [displayData]
  );

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 200,
        headerClassName: 'data-grid-header',
      },
      {
        field: 'chatId',
        headerName: 'Chat ID',
        width: 200,
        headerClassName: 'data-grid-header',
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 200,
        headerClassName: 'data-grid-header',
      },
      ...uniqueDataKeys.map((key: any) => ({
        field: key,
        headerName: key.charAt(0).toUpperCase() + key.slice(1),
        width: 200,
        headerClassName: 'data-grid-header',
        renderCell: (params: any) => {
          if (params.value === undefined) return '-';

          if (key === 'CustomerCallerNumber' && params.value) {
            const numStr = String(params.value);
            if (numStr.length > 10) {
              const lastTen = numStr.slice(-10);
              const countryCode = numStr.slice(0, -10);
              return `(${countryCode})${lastTen}`;
            }
          }
          return String(params.value);
        },
      })),
    ],
    [uniqueDataKeys]
  );

  // Handle pagination model changes
  const handlePaginationModelChange = (newModel: typeof paginationModel) => {
    setPaginationModel(newModel);
  };

  // Add CSV download function
  const downloadCSV = () => {
    if (!displayData.length) {
      return;
    }

    // Get all unique fields from all appointments (including nested data)
    const allFields = new Set<string>();
    displayData.forEach((appointment: any) => {
      // Add top-level fields
      Object.keys(appointment).forEach(key => {
        if (key !== 'data') {
          // Exclude the data object itself
          allFields.add(key);
        }
      });

      // Add fields from the data object
      if (appointment.data && typeof appointment.data === 'object') {
        Object.keys(appointment.data).forEach(key => allFields.add(key));
      }
    });

    // Sort fields for consistent ordering
    const sortedFields = Array.from(allFields).sort();
    const csvHeaders = sortedFields.map(
      field => field.charAt(0).toUpperCase() + field.slice(1)
    );

    // Create CSV rows
    const csvRows = displayData.map((appointment: any) => {
      const row: string[] = [];

      sortedFields.forEach((fieldName: string) => {
        let value;

        // Check if field exists in top-level appointment object
        if (fieldName in appointment) {
          value = appointment[fieldName];
        }
        // Check if field exists in the data object
        else if (appointment.data && fieldName in appointment.data) {
          value = appointment.data[fieldName];
        }
        // Field doesn't exist
        else {
          value = null;
        }

        // Handle different value types
        if (value === undefined || value === null) {
          row.push('');
        } else if (fieldName === 'createdAt' || fieldName === 'updatedAt') {
          // Format dates and wrap in quotes to handle commas
          const formattedDate = new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
          row.push(`"${formattedDate}"`);
        } else if (typeof value === 'object') {
          // Handle objects and arrays by converting to JSON string
          const jsonString = JSON.stringify(value).replace(/"/g, '""');
          row.push(`"${jsonString}"`);
        } else {
          // Convert to string and escape quotes
          const stringValue = String(value).replace(/"/g, '""');
          row.push(`"${stringValue}"`);
        }
      });

      return row;
    });

    // Combine headers and rows
    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.join(','))
      .join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'extraction_data.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Box sx={{ width: '100%', padding: '0px 24px 24px', mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 2,
          padding: '15px 0',
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontSize: '16px', fontWeight: '500', color: 'custom.textBlue' }}
        >
          Data Extraction History
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<DownloadSimpleIcon />}
            variant="outlined"
            color="primary"
            disabled={!displayData.length}
            onClick={downloadCSV}
            sx={{ fontWeight: 500, borderRadius: '6px', textTransform: 'none' }}
          >
            Download CSV
          </Button>
        </Box>
      </Box>
      {isTableLoading ? (
        <CallsDataSkeleton />
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={row => row.id}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[25, 50, 100]}
          rowCount={totalColumns}
          getRowHeight={() => 60}
          paginationMode="server"
          loading={isTableLoading}
          disableRowSelectionOnClick
          disableColumnFilter={false}
          disableColumnSelector={false}
          disableDensitySelector={false}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            loadingOverlay: CallsDataSkeleton,
          }}
          sx={{
            border: `1px solid ${theme.palette.lightGray.main}`,
            borderRadius: '12px',
            '--DataGrid-overlayHeight': '300px',
            height: '100%',
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto !important',
            '& .MuiDataGrid-main': {
              overflow: 'auto',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto !important',
            },
            '& .data-grid-header': {
              bgcolor: '#F8F9FC',
              color: 'custom.textBlue',
              fontSize: '14px',
              fontWeight: 600,
              padding: '12px 16px',
            },
            '& .MuiDataGrid-cell': {
              padding: '12px 16px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              '&:focus, &:focus-within': {
                outline: 'none !important',
              },
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
          }}
        />
      )}
    </Box>
  );
};

const StyledGridOverlay = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  '& .no-rows-primary': {
    fill: '#3D4751',
    ...theme.applyStyles('light', {
      fill: '#AEB8C2',
    }),
  },
  '& .no-rows-secondary': {
    fill: '#1D2126',
    ...theme.applyStyles('light', {
      fill: '#E8EAED',
    }),
  },
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        width={96}
        viewBox="0 0 452 257"
        aria-hidden
        focusable="false"
      >
        <path
          className="no-rows-primary"
          d="M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z"
        />
        <path
          className="no-rows-primary"
          d="M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z"
        />
        <path
          className="no-rows-primary"
          d="M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z"
        />
        <path
          className="no-rows-secondary"
          d="M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z"
        />
      </svg>
      <Box sx={{ mt: 2, color: 'text.secondary' }}>No Data Available</Box>
    </StyledGridOverlay>
  );
}

export default ExtractionDataTable;
